import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { handleSearchData } from '../../utils/analytics';

function FilterCheckbox({
	name, isSelected, handleOnClick, idx, total,
}) {
	return (
		<div className="recipe-filter-item">
			{/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
			<label>
				<input
					type="checkbox"
					onClick={handleOnClick}
					className={`recipe-filter-checkbox ${isSelected ? 'checked' : ''}`}
				/>
				<span className="recipe-filter-item-name">{name}</span>
				<span className="visually-hidden">{`(${idx + 1} of ${total})`}</span>
			</label>
		</div>
	);
}

FilterCheckbox.propTypes = {
	name: PropTypes.string.isRequired,
	isSelected: PropTypes.bool.isRequired,
	handleOnClick: PropTypes.func.isRequired,
	idx: PropTypes.number.isRequired,
	total: PropTypes.number.isRequired,
};

function RecipeFilterList({
	title, filterItems, filter, isSelected, handleOnClick,
}) {
	const items = filterItems[filter].nodes;

	return (
		<fieldset className="recipe-filter-list">
			<legend className="visually-hidden">{`${title} (${items.length} items)`}</legend>
			{items.map((item, i) => (
				<React.Fragment key={item.id}>
					<FilterCheckbox
						id={item.id}
						name={item.name}
						isSelected={isSelected(item)}
						handleOnClick={() => handleOnClick(item)}
						idx={i}
						total={items.length}
					/>
				</React.Fragment>
			))}
		</fieldset>
	);
}

RecipeFilterList.propTypes = {
	title: PropTypes.string.isRequired,
	filterItems: PropTypes.object.isRequired,
	filter: PropTypes.string.isRequired,
	isSelected: PropTypes.func.isRequired,
	handleOnClick: PropTypes.func.isRequired,
};

function RecipeFilterGroup({
	filtersData, filter, filterItems, isSelected, handleOnClick,
}) {
	const { title } = filtersData[filter];

	return (
		<div className="recipe-filters-mobile-filter">
			<h3 className="recipe-filter-title">{title}</h3>
			<RecipeFilterList
				title={title}
				filterItems={filterItems}
				filter={filter}
				isSelected={isSelected}
				handleOnClick={handleOnClick}
			/>
		</div>
	);
}

RecipeFilterGroup.propTypes = {
	filtersData: PropTypes.object.isRequired,
	filterItems: PropTypes.object.isRequired,
	filter: PropTypes.string.isRequired,
	isSelected: PropTypes.func.isRequired,
	handleOnClick: PropTypes.func.isRequired,
};

function AllRecipeFilterGroups({
	filtersData, filterItems, isSelected, handleOnClick,
}) {
	return (
		<div className="recipe-filters-modal-body">
			{Object.keys(filtersData).map((filter) => (
				<React.Fragment key={filter}>
					<RecipeFilterGroup
						filtersData={filtersData}
						filter={filter}
						filterItems={filterItems}
						isSelected={isSelected}
						handleOnClick={handleOnClick}
					/>
				</React.Fragment>
			))}
		</div>
	);
}

AllRecipeFilterGroups.propTypes = {
	filtersData: PropTypes.object.isRequired,
	filterItems: PropTypes.object.isRequired,
	isSelected: PropTypes.func.isRequired,
	handleOnClick: PropTypes.func.isRequired,
};

export default function RecipeFiltersModal({
	showModal, closeModal, filterItems, filtersData, filters, setFilters,
}) {
	const [selection, setSelection] = useState([]);

	useEffect(() => {
		// Set the class of the body depending on whether the modal is open or not
		if (showModal) {
			document.body.classList.add('modal-open');
		} else {
			document.body.classList.remove('modal-open');
		}
	});

	useEffect(() => {
		// Check if any filters have been unselected
		setSelection([...filters]);
	}, [filters]);

	if (!showModal) {
		return null;
	}

	const close = () => {
		// Revert selected filters before closing
		setSelection([...filters]);
		closeModal();
	};

	const clear = () => {
		setSelection([]);
	};

	const handleOnClick = (item) => {
		if (!selection.some((current) => current.id === item.id)) {
			setSelection([...selection, item]);
		} else {
			let selectionAfterRemoval = selection;

			selectionAfterRemoval = selectionAfterRemoval.filter(
				(current) => current.id !== item.id,
			);

			setSelection(selectionAfterRemoval);
		}
	};

	const isSelected = (item) => {
		if (selection.find((current) => current.id === item.id)) {
			return true;
		}

		return false;
	};

	const save = () => {
		let newFilters = [...filters];

		// Add new filters
		selection.forEach((item) => {
			if (!filters.some((current) => current.id === item.id)) {
				newFilters = [...newFilters, item];
			}
		});

		// Remove unselected filters
		newFilters = newFilters.filter(
			(item) => selection.some((current) => current.id === item.id),
		);

		setFilters(newFilters);
		closeModal();
		handleSearchData();
	};

	return (
		<div className="recipe-filters-modal">
			<div className="recipe-filters-modal-content">
				<div className="recipe-filters-modal-header">
					<div className="recipe-filters-modal-header-item">
						<button
							type="button"
							className="recipe-filters-modal-close"
							onClick={() => { close(); }}
							aria-label="Close the modal"
						>
							&nbsp;
						</button>
					</div>
					<div className="recipe-filters-modal-header-item">
						<span className="recipe-filters-modal-title">Refine Results</span>
					</div>
					<div className="recipe-filters-modal-header-item">
						<button
							type="button"
							className="recipe-filters-modal-clear"
							onClick={() => { clear(); }}
						>
							Clear all
						</button>
					</div>
				</div>
				<AllRecipeFilterGroups
					filtersData={filtersData}
					filterItems={filterItems}
					isSelected={isSelected}
					handleOnClick={handleOnClick}
				/>
				<div className="recipe-filters-modal-footer">
					<button
						type="button"
						className="recipe-filters-modal-save"
						onClick={() => { save(); }}
					>
						Show Results
					</button>
				</div>
			</div>
		</div>
	);
}

RecipeFiltersModal.propTypes = {
	showModal: PropTypes.bool.isRequired,
	closeModal: PropTypes.func.isRequired,
	filterItems: PropTypes.object.isRequired,
	filtersData: PropTypes.object.isRequired,
	filters: PropTypes.array.isRequired,
	setFilters: PropTypes.func.isRequired,
};
